/**
 * NoTranslate - protects data-derived proper nouns from browser translation
 *
 * Chrome/Google Translate rewrites Czech proper nouns it mistakes for common
 * words (e.g. the town "Most" -> "Většina", "strana" (party) -> "stránka").
 * `translate="no"` + `class="notranslate"` together stop Google Translate
 * from touching an element's content, even when the user has "always
 * translate this site" enabled (the page-level `lang="cs"` fix alone does
 * not help those users).
 *
 * Only wrap API-derived proper nouns (municipality/party/candidate names)
 * with this - never wrap our own static Czech UI copy, which should stay
 * translatable for non-Czech speakers.
 */
import React from 'react';

type NoTranslateProps<T extends React.ElementType> = {
  as?: T;
  children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, 'as' | 'children'>;

function NoTranslate<T extends React.ElementType = 'span'>({
  as,
  children,
  className,
  ...rest
}: NoTranslateProps<T>) {
  const Component = (as || 'span') as React.ElementType;
  const combinedClassName = className ? `notranslate ${className}` : 'notranslate';

  return (
    <Component translate="no" className={combinedClassName} {...rest}>
      {children}
    </Component>
  );
}

export default NoTranslate;
